// Castintech dashboard — Toast UI helper.
// Faz 9b PR9 — minik notification bar.
// 3.5s sonra otomatik kalkar. type: 'info' | 'success' | 'error' | 'warning'.
// Faz 13 — typed.
// Aşama D1 (2026-05-24) — a11y patch:
//   - container: role="status" + aria-live="polite" (screen reader duyurur)
//   - error toast: role="alert" + aria-live="assertive" (yüksek öncelik)
//   - exit animation (toast-exit class — CSS transition ile)
//   - prefers-reduced-motion saygısı (CSS @media globalde 0.01ms)

export type ToastType = 'info' | 'success' | 'error' | 'warning';

const TOAST_LIFETIME_MS = 3500;
const TOAST_EXIT_MS = 240;   // exit animation süresi (CSS .toast-exit transition ile sync)

/**
 * Toast container'ı oluşturur (yoksa) ve döndürür.
 * Container'a aria-live attribute set edilir — DOM'a eklenen toast'lar
 * screen reader tarafından otomatik duyurulur (polite mode default).
 */
function ensureToastContainer(): HTMLElement | null {
  let c = document.getElementById('toast-container');
  if (c) {
    // Mevcut container'a a11y attribute'larını idempotent ekle.
    // Defensive: test mock'larında hasAttribute/setAttribute method'u
    // olmayabilir (tests/api-error-boundary.test.js stub pattern).
    // Method varsa kullan, yoksa attribute eklemeyi sessiz atla.
    if (typeof c.hasAttribute === 'function' && typeof c.setAttribute === 'function') {
      if (!c.hasAttribute('role')) c.setAttribute('role', 'status');
      if (!c.hasAttribute('aria-live')) c.setAttribute('aria-live', 'polite');
      if (!c.hasAttribute('aria-atomic')) c.setAttribute('aria-atomic', 'false');
    }
    return c;
  }
  // Container HTML'de tanımlı değilse otomatik oluştur (defensive)
  c = document.createElement('div');
  c.id = 'toast-container';
  if (typeof c.setAttribute === 'function') {
    c.setAttribute('role', 'status');
    c.setAttribute('aria-live', 'polite');
    c.setAttribute('aria-atomic', 'false');
  }
  document.body.appendChild(c);
  return c;
}

/**
 * Toast bildirimi göster. 3.5s sonra otomatik kalkar; exit animation 240ms.
 *
 * A11y:
 *   - 'error' tipi `role="alert"` + `aria-live="assertive"` (hemen duyurulur)
 *   - Diğerleri parent container'ın `aria-live="polite"` kullanır (sıralı)
 *
 * Motion:
 *   - Exit animation `.toast-exit` class ile CSS transition (240ms)
 *   - prefers-reduced-motion: CSS @media globalde 0.01ms (sert remove gibi)
 */
export function toast(msg: string, type: ToastType = 'info'): void {
  const c = ensureToastContainer();
  if (!c) return;
  const el = document.createElement('div');
  el.className = `toast ${type}`;
  el.textContent = msg;

  // Error toast yüksek öncelikli — screen reader hemen duyurmalı.
  // Defensive: test mock'ında setAttribute olmayabilir, sessiz atla.
  if (type === 'error' && typeof el.setAttribute === 'function') {
    el.setAttribute('role', 'alert');
    el.setAttribute('aria-live', 'assertive');
  }

  c.appendChild(el);

  // Exit animation: 3.5s sonra .toast-exit class ekle, 240ms sonra DOM'dan kaldır.
  // Defensive: test mock'larında classList veya remove method'u olmayabilir.
  setTimeout(() => {
    if (el.classList && typeof el.classList.add === 'function') {
      el.classList.add('toast-exit');
    }
    setTimeout(() => {
      if (typeof el.remove === 'function') el.remove();
    }, TOAST_EXIT_MS);
  }, TOAST_LIFETIME_MS);
}
